/** @format */

/* eslint @typescript-eslint/prefer-string-starts-ends-with: "warn" */
import { TFunction } from 'i18next';

import { SearchFacet } from '@toucan/common/services/search/constants';
import {
    FilterQuery,
    Query,
    QueryResponse,
    SearchListingProduct,
    SortBy,
} from '@toucan/common/services/search/types';

import { toCamelCase } from '@toucan/client/global/axios';
import { MarketCode } from '@toucan/client/global/market';
import { SearchParam } from '@toucan/client/global/nextjs';

import {
    InventoryTags,
    ProductTagInformation,
    getProductTags,
} from '@toucan/client/core/inventory';
import { getListingResults } from '@toucan/client/core/search';

import { Filter, FilterOption, SortByOption } from '../config/types';

export function getModelIdFromQuery(filterQuery: FilterQuery) {
    const modelId = Number(
        (filterQuery.modelId as string)?.replaceAll('"', '')
    );

    return isNaN(modelId) ? null : modelId;
}

/**
 * Transforms filter values into a format Solr can recognise
 */
export function transformToSearchValues(
    key: string,
    value: string | string[] | number | boolean
): string | string[] | number | boolean {
    if ((key as SearchFacet) === SearchFacet.productPriceModifier) {
        if (Array.isArray(value)) {
            return `(${value.join(' AND ')})`;
        }

        return value;
    }

    // Known numerical facets that don't conform to a predictable naming
    // convention
    const knownNumericFacets = ['productPrice'];
    const solrSpecialCharactersRegex =
        /(?=\+|&&|\|{2}|!|\(|\)|{|}|\^|~|\?|\/)/g;

    if (Array.isArray(value)) {
        return value.map((value) =>
            transformToSearchValues(key, value)
        ) as string[];
    }

    // Prepare any numerical facet value
    if (knownNumericFacets.includes(key) || /Num$/.test(key)) {
        const stringValue = String(value);

        if (stringValue.includes('-')) {
            const statement = /-$/.test(stringValue) ? ' TO *' : ' TO ';
            const rangeQuery = stringValue.replace('-', statement);
            return `[${rangeQuery}]`;
        }

        return value;
    }

    // We need to wrap any filter option values in double-quotes so that
    // Solr interprets them as literal values instead of trying to handle
    // special characters
    // https://solr.apache.org/guide/8_4/the-standard-query-parser.html#escaping-special-characters
    if (typeof value === 'string') {
        return value.replace(solrSpecialCharactersRegex, '\\');
    }

    return value;
}

/**
 * Escape Solr query
 *
 * This escapes the '-' and standard brackets characters so that it is considered literally
 * and not as an _excludes_ or grouped search.
 */
export function escapeSolrQuery(query?: string): string | undefined {
    if (!query) {
        return;
    }
    // '\' is escape character in js, so '\\' is a literal '\' string

    // escape hyphens with double slash
    let result = query.replace(/(^|\W)(-)\b/g, '$1\\$2');

    // escape brackets with double slash
    result = result.replace(/(\(|\))/g, '\\$1');

    return result;
}

export function transformSortByToSortByOption({
    sort,
    sortOptions,
    defaultSortOption,
}: {
    sort: SortBy;
    sortOptions: SortByOption[];
    defaultSortOption: SortByOption;
}): SortByOption {
    const facet = Object.keys(sort)[0];
    const direction = sort[facet];

    return (
        sortOptions.find((option) => {
            const isSameDirection = option.direction === direction;
            const isSameFacet = toCamelCase(option.facet) === facet;

            return isSameDirection && isSameFacet;
        }) ?? defaultSortOption
    );
}

export function getNumRows({
    page,
    lastFetchedPage,
    totalResults,
    resultsPerPage,
    isViewMore,
}: {
    page: number;
    lastFetchedPage: number;
    totalResults: number;
    resultsPerPage: number;
    isViewMore: boolean;
}) {
    if (!isViewMore) return resultsPerPage;

    const isInitialLoad = lastFetchedPage === 0;
    const isLastPage = page * resultsPerPage >= totalResults;

    const rows = (page - lastFetchedPage) * resultsPerPage || resultsPerPage;

    const areRemainingResultsEqualToResultsPerPage =
        isLastPage && rows === resultsPerPage;

    if (
        isInitialLoad ||
        !isLastPage ||
        areRemainingResultsEqualToResultsPerPage
    ) {
        return rows;
    }

    const numCompletePages = Math.floor(totalResults / resultsPerPage);
    const numRowsOnLastPage = totalResults - numCompletePages * resultsPerPage;
    const isOnlyPage = page - lastFetchedPage === 1;

    return isOnlyPage
        ? numRowsOnLastPage
        : numRowsOnLastPage + (rows - resultsPerPage);
}

const validateQueryObject = (queryObject: object) => {
    for (const queryParameter of Object.values(queryObject)) {
        if (typeof queryParameter === 'object') {
            if (!validateQueryObject(queryParameter)) {
                return false;
            }
        }

        const isUndefinedQueryParameter = /undefined/i.test(queryParameter);

        if (isUndefinedQueryParameter) {
            return false;
        }
    }

    return true;
};

/**
 * Determines whether or not the baseQuery has valid parameters
 */
export const validateBaseQuery = (baseQuery: Partial<Query>) => {
    if (Object.keys(baseQuery).length === 0) {
        return false;
    }

    return validateQueryObject(baseQuery);
};

export function getListingQuerySortBy(
    option: SortByOption,
    isProductListingPage: boolean
): SortBy {
    const options = {};
    if (option.facet === 'relevance') {
        return options;
    }
    // @ts-ignore - Element implicitly has an any type - Touch and Change
    options[option.facet] = option.direction;

    // Search service accepts mutliple sort options
    // If multiple values are passed, they are prioritised in the order they are passed,
    // so the first is the primary sort, etc.
    // On product pages we want to add a 2nd sort option by sku if the product_price is the primary
    // sort option. This is so that we display older products higher up in the list
    if (isProductListingPage && option.facet === 'product_price') {
        // @ts-ignore - Element implicitly has an any type - Touch and Change
        options.product_sku = 'ASC';
    }

    return options;
}

export function transformFacetsOptionValue({
    value,
    bottomBoundaryLanguage,
    topBoundaryLanguage,
    t,
}: {
    value: string;
    bottomBoundaryLanguage?: (value: string) => string;
    topBoundaryLanguage?: (value: string) => string;
    t: TFunction;
}) {
    const startsWithZero = value.startsWith('0-');
    const endsWithNothing = value.endsWith('-');

    if (startsWithZero) {
        value = value.replace(/^0-/, '');
        value = bottomBoundaryLanguage
            ? bottomBoundaryLanguage(value)
            : t('Under {{value}}', {
                  value,
                  context: 'search-filters',
              });
    }

    if (endsWithNothing) {
        value = value.replace('-', '');
        value = topBoundaryLanguage
            ? topBoundaryLanguage(value)
            : t('Over {{value}}', {
                  value,
                  context: 'search-filters',
              });
    }

    return value.replace('-', ' - ');
}

export function trimFilterOptions(options: FilterOption[]): FilterOption[] {
    let firstQuantityIndexSet = false;
    let firstQuantityIndex = 0;
    let lastQuantityIndex = 0;

    // Find the index of the first and last option that has a quantity
    options.forEach((option, index) => {
        if (option.quantity) {
            if (firstQuantityIndexSet === false) {
                firstQuantityIndexSet = true;
                firstQuantityIndex = index;
            }

            lastQuantityIndex = index;
        }
    });

    return options.slice(firstQuantityIndex, lastQuantityIndex + 1);
}

/**
 * Transforms an array of filters into a searchParam
 *
 * @param {Filter[]} filters An array of filter to be transformed into a searchParam
 * @returns {SearchParam}
 */
export function transformFiltersToSearchParams(filters: Filter[]): SearchParam {
    const additionalQuery = {};
    const filterQuery = {};
    filters.forEach((filter) => {
        // @ts-ignore - Element implicitly has an any type - Touch and Change
        filterQuery[filter.id] = filter.selectedOptions.map(
            (option: FilterOption) => {
                return option.value;
            }
        );

        if (filter.selectedVariant) {
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            additionalQuery[filter.id] = filter.selectedVariant;
        }
    });
    return { filterQuery, ...additionalQuery };
}

export async function getListingResultsWithTags<T extends SearchListingProduct>(
    search: Query,
    locale: string,
    marketCode: MarketCode,
    shouldFetchTags: boolean
): Promise<QueryResponse<T>> {
    const results = await getListingResults<T>(search, locale, marketCode);

    const productIds = results.results.map((result: T) =>
        Number(result.productId?.values[0])
    );

    let productTagInfo: InventoryTags = [];

    if (shouldFetchTags) {
        try {
            productTagInfo = await getProductTags(productIds, marketCode);
        } catch (error) {
            console.error('Failed to fetch product tags: ', error);
        }
    }

    const resultsWithTags = results.results.map((result: T) => {
        const productId = Number(result.productId?.values[0]);
        const tagData = productTagInfo.find(
            (item: ProductTagInformation) => item.productId === productId
        );

        return {
            ...result,
            tags: {
                values: tagData?.tags ?? [],
            },
        };
    });

    return {
        ...results,
        results: resultsWithTags,
    };
}
